import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import { useTranslations } from '../hooks/useTranslations';
import AudioRecorder from './editor/AudioRecorder';
import AudioWaveform from './editor/AudioWaveform';
import { downloadFile, processMessage } from '../api';


const ChatPanel = () => {
  const { 
    chatHistory, 
    addChatMessage, 
    loading, 
    setLoading,
    sessionHistory,
    language,
    
    // session
    currentSessionId, 

    addToImageHistory,
    appendCurrentData,
  } = useAppStore();

  const { t } = useTranslations();
  const [message, setMessage] = useState('');
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashIndex, setSlashIndex] = useState(0);
  const [showAudioMessage, setShowAudioMessage] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Auto-grow textarea height based on content
  useEffect(() => {
    if (!textareaRef.current) return;
    const el = textareaRef.current;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  }, [message]);

  
  const PRESET_COMMANDS = [
    { key: 'enhance', label: t('enhancePhoto'), prompt: 'enhance overall photo quality: fix exposure, improve contrast and saturation, reduce noise' },
    { key: 'sharpen', label: t('sharpenDetails'), prompt: 'deblur image and increase clarity while avoiding halos' },
    { key: 'bokeh', label: t('bokehEffect'), prompt: 'bokeh effect' },
  ];

  const filteredCommands = showSlashMenu
    ? PRESET_COMMANDS.filter(c => c.label.toLowerCase().includes(slashQuery.toLowerCase()) || c.key.includes(slashQuery.toLowerCase()))
    : [];

  const insertCommand = (cmd) => {
    setMessage(cmd.prompt);
    setShowSlashMenu(false);
    setSlashQuery('');
  };

  const handleSendMessage = async (forcedMessage) => {
    const text = (forcedMessage ?? message).trim();
    
    // Check if we have text to send
    if (!text) {
      console.log('❌ No text to send');
      return;
    }
    
    // Check if we're currently loading
    if (loading) {
      console.log('❌ Currently loading, cannot send message');
      return;
    }
    
    // Check if we have an active session
    if (!currentSessionId) {
      console.log('❌ No active session. ');
      return;
    }

    const userMessage = text;
    if (!forcedMessage) setMessage('');

    setLoading(true);

    // Add user message to chat
    addChatMessage({
      type: 'user',
      content: userMessage,
      timestamp: Date.now()
    });

    try {
      var result = null
      if (sessionHistory.length > 0) {
        console.log('💬 Sending chat message to backend...', currentSessionId, sessionHistory.at(-1).currentDataId, sessionHistory.at(-1).currentAuxDataId, userMessage);
        result = await processMessage(currentSessionId, sessionHistory.at(-1).currentDataId, sessionHistory.at(-1).currentAuxDataId, userMessage) 
      } else {
        console.log('💬 Sending chat message to backend...', currentSessionId, null, null, userMessage);
        result = await processMessage(currentSessionId, null, null, userMessage) 
      }
      
      console.log('taskResult:', result);
      await appendCurrentData(result.task.output_data_id, null)

      const image = await downloadFile(result.task.output_data_id)
      await addToImageHistory(image, userMessage)
      
      // Add AI response
      addChatMessage({
        type: 'ai',
        content: result.message.response	,
        timestamp: Date.now()
      });

    } catch (error) {
      console.error('❌ Chat error:', error);
      addChatMessage({
        type: 'ai',
        content: 'Sorry, I encountered an error processing your request.',
        timestamp: Date.now()
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle audio transcription completion
  const handleTranscriptionComplete = (transcribedText, confidence) => {
    console.log('🎤 Audio transcribed:', transcribedText, 'Confidence:', confidence);
    
    // Show a brief audio message indicator
    setShowAudioMessage(true);
    setTimeout(() => setShowAudioMessage(false), 3000);
    
    // Set the transcribed text in the input field
    setMessage(transcribedText);
    
    // Force the textarea to update its value and trigger change events
    if (textareaRef.current) {
      textareaRef.current.value = transcribedText;
      // Trigger input event to ensure React state is updated
      const event = new Event('input', { bubbles: true });
      textareaRef.current.dispatchEvent(event);
      
      // Focus the textarea so user can immediately edit or send
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(transcribedText.length, transcribedText.length);
    }
    
    // Optionally auto-send if confidence is high
    if (confidence > 0.8) {
      setTimeout(() => {
        handleSendMessage(transcribedText);
      }, 500);
    }
  };

  // Handle recording state changes
  const handleRecordingStateChange = (recording, level = 0) => {
    setIsRecording(recording);
    setAudioLevel(level);
  };

  const handleKeyPress = (e) => {
    // Slash command detection and navigation
    if (e.key === '/' && e.target.selectionStart === 1 && e.target.value === '/') {
      setShowSlashMenu(true);
      setSlashQuery('');
      setSlashIndex(0);
      return;
    }
    if (showSlashMenu) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIndex((i) => Math.min(i + 1, filteredCommands.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSlashIndex((i) => Math.max(i - 1, 0)); return; }
      if (e.key === 'Tab') { e.preventDefault(); insertCommand(filteredCommands[slashIndex] || PRESET_COMMANDS[0]); return; }
      if (e.key === 'Enter') { e.preventDefault(); insertCommand(filteredCommands[slashIndex] || PRESET_COMMANDS[0]); return; }
      // Update query on normal character input
      setTimeout(() => {
        const value = textareaRef.current?.value || '';
        const q = value.slice(1); // remove leading '/'
        setSlashQuery(q);
      }, 0);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };


  return (
    <div className="chat-panel">
      <div className="chat-messages">
        {/* System welcome message (minimal, professional) */}
        <div className="message system-message">
          <div className="message-content system-text">
                            <p>{t('greeting')}</p>
          </div>
        </div>
        
        {/* Chat history */}
        {chatHistory.map((msg, index) => (
          <div key={index} className={`message ${msg.type === 'user' ? 'user' : ''}`}>
            <div className="message-content message-text">
              {msg.content}
            </div>
          </div>
        ))}
        
        {/* Audio transcription indicator */}
        {showAudioMessage && (
          <div className="message audio-message">
            <div className="message-content audio-indicator">
              <span className="audio-icon">🎤</span>
              <span>Voice input received</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-container">
        <div className="chat-input">
          {/* Quick chips to invite users to try */}
          <div className="quick-chips">
            {PRESET_COMMANDS.slice(0, 6).map((c) => (
              <button
                key={c.key}
                className="chip-btn"
                disabled={loading}
                onClick={() => handleSendMessage(c.prompt)}
                title={c.label}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* Audio waveform animation */}
          {isRecording && (
            <div className="audio-waveform-container">
              <AudioWaveform isRecording={isRecording} audioLevel={audioLevel} />
            </div>
          )}
          
          <textarea
            id="chatInput"
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => { if (message.startsWith('/')) { setShowSlashMenu(true); setSlashQuery(message.slice(1)); } }}
                            placeholder={currentSessionId ? t('describeEdit') : "Upload or select an image to start editing..."}
            disabled={loading}
            rows={3}
            style={{ 
              resize: 'none',
              opacity: currentSessionId ? 1 : 0.6
            }}
          />
          {showSlashMenu && filteredCommands.length > 0 && (
            <div className="slash-hints">
              {filteredCommands.map((c, idx) => (
                <button
                  key={c.key}
                  className={`slash-item ${idx === slashIndex ? 'active' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); insertCommand(c); }}
                >
                  <span className="k">/{c.key}</span>
                  <span className="l">{c.label}</span>
                </button>
              ))}
            </div>
          )}
          <div className="chat-actions">
            <button 
              className="action-btn"
              onClick={() => {
                if (chatHistory.length > 0) {
                  const chatData = {
                    sessionId: currentSessionId,
                    timestamp: new Date().toISOString(),
                    chatHistory: chatHistory,
                    sessionHistory: sessionHistory
                  };
                  const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `esper-chat-${Date.now()}.json`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }
              }}
              disabled={chatHistory.length === 0}
              title="Save Chat"
            >
              <i className="fas fa-save"></i>
            </button>

            {/* Audio recorder */}
            <AudioRecorder 
              onTranscriptionComplete={handleTranscriptionComplete}
              onRecordingStateChange={handleRecordingStateChange}
              disabled={loading}
              language={language}
            />
            <button 
              className="send-btn" 
              onClick={() => handleSendMessage()}
              disabled={!message.trim() || loading}
              title={
                !currentSessionId ? "Upload an image first to start editing" :
                !message.trim() ? "Type a message to send" :
                loading ? "Processing..." :
                "Send message"
              }
            >
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel; 